import {Orientation, PrintImageOffset} from "./print-utils";

export interface PrintPreviewCanvasState {
    printScale: number;
    imageOffset: PrintImageOffset;
    imageRotation: number;
}

export const DEFAULT_PRINT_PREVIEW_STATE: PrintPreviewCanvasState = {
    printScale: 1,
    imageOffset: {x: 0, y: 0},
    imageRotation: 0,
};

export interface PreviewRect {
    width: number;
    height: number;
    left: number;
    top: number;
}

export interface PreviewPanBounds {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
}

export function buildPreviewViewportSize(
    hostWidth: number,
    windowWidth: number,
    windowHeight: number,
    orientation: Orientation,
) {
    const viewportHeight = Math.max(260, Math.round(windowHeight * 0.55));
    const ratio = orientation === 'landscape' ? (297 / 210) : (210 / 297);
    const maxViewportWidth = Math.max(260, hostWidth || Math.round(windowWidth * 0.5));
    const width = Math.min(Math.round(viewportHeight * ratio), maxViewportWidth);
    const height = Math.round(width / ratio);
    return {width, height};
}

export function buildPreviewFitRect(
    viewportSize: {width: number; height: number},
    imageNaturalSize: {width: number; height: number},
    orientation: Orientation,
    imageRotation: number,
): PreviewRect {
    if (!viewportSize.width || !viewportSize.height) {
        return {width: 0, height: 0, left: 0, top: 0};
    }

    const naturalWidth = imageNaturalSize.width || (orientation === 'landscape' ? 297 : 210);
    const naturalHeight = imageNaturalSize.height || (orientation === 'landscape' ? 210 : 297);
    const isQuarterTurn = imageRotation === 90 || imageRotation === 270;
    const sourceWidth = isQuarterTurn ? naturalHeight : naturalWidth;
    const sourceHeight = isQuarterTurn ? naturalWidth : naturalHeight;
    const imageRatio = sourceWidth / sourceHeight;
    const frameRatio = viewportSize.width / viewportSize.height;

    let width = viewportSize.width;
    let height = viewportSize.height;

    if (imageRatio > frameRatio) {
        height = width / imageRatio;
    } else {
        width = height * imageRatio;
    }

    return {
        width,
        height,
        left: (viewportSize.width - width) / 2,
        top: (viewportSize.height - height) / 2,
    };
}

export function buildPreviewDrawRect(
    fitRect: PreviewRect,
    printScale: number,
): PreviewRect {
    const width = fitRect.width * printScale;
    const height = fitRect.height * printScale;

    return {
        width,
        height,
        left: fitRect.left - ((width - fitRect.width) / 2),
        top: fitRect.top - ((height - fitRect.height) / 2),
    };
}

export function buildPreviewPanBounds(
    drawRect: PreviewRect,
    viewportSize: {width: number; height: number},
): PreviewPanBounds {
    return {
        minX: Math.min(-drawRect.left, viewportSize.width - (drawRect.left + drawRect.width)),
        maxX: Math.max(-drawRect.left, viewportSize.width - (drawRect.left + drawRect.width)),
        minY: Math.min(-drawRect.top, viewportSize.height - (drawRect.top + drawRect.height)),
        maxY: Math.max(-drawRect.top, viewportSize.height - (drawRect.top + drawRect.height)),
    };
}

export function clampPreviewPan(
    nextPanX: number,
    nextPanY: number,
    panBounds: PreviewPanBounds,
) {
    return {
        x: Math.max(panBounds.minX, Math.min(panBounds.maxX, nextPanX)),
        y: Math.max(panBounds.minY, Math.min(panBounds.maxY, nextPanY)),
    };
}

export function getPreviewPanFromOffset(
    imageOffset: PrintImageOffset,
    panBounds: PreviewPanBounds,
) {
    return {
        x: imageOffset.x >= 0
            ? imageOffset.x * Math.max(0, panBounds.maxX)
            : imageOffset.x * Math.max(0, -panBounds.minX),
        y: imageOffset.y >= 0
            ? imageOffset.y * Math.max(0, panBounds.maxY)
            : imageOffset.y * Math.max(0, -panBounds.minY),
    };
}

export function getPreviewOffsetFromPan(
    pan: {x: number; y: number},
    panBounds: PreviewPanBounds,
): PrintImageOffset {
    return {
        x: pan.x >= 0
            ? (panBounds.maxX > 0 ? pan.x / panBounds.maxX : 0)
            : (panBounds.minX < 0 ? pan.x / -panBounds.minX : 0),
        y: pan.y >= 0
            ? (panBounds.maxY > 0 ? pan.y / panBounds.maxY : 0)
            : (panBounds.minY < 0 ? pan.y / -panBounds.minY : 0),
    };
}
